import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { cva, type VariantProps } from 'class-variance-authority';

import { SortableHeader } from './DataTable';
import { cn } from './utils';

// Inline Badge component - matches @loader/ui Badge variants for consistency
const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 transition-colors',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground',
        destructive:
          'border-transparent bg-destructive text-white',
        outline: 'text-foreground',
        success:
          'border-transparent bg-green-500/10 text-green-700 dark:text-green-400',
        warning:
          'border-transparent bg-amber-500/10 text-amber-700 dark:text-amber-400',
        info:
          'border-transparent bg-blue-500/10 text-blue-700 dark:text-blue-400',
        muted:
          'border-transparent bg-muted text-muted-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

// Base column options interface
export interface BaseColumnOptions {
  minWidth?: number;
  maxWidth?: number;
  enableSorting?: boolean;
  enableFiltering?: boolean;
  enableResizing?: boolean;
  enableHiding?: boolean;
  cellRenderer?: (value: unknown, original: unknown) => React.ReactNode;
  headerRenderer?: (header: string) => React.ReactNode;
  filterFn?: string;
  sortFn?: string;
  className?: string;
  headerClassName?: string;
  cellClassName?: string;
}

// Generic column creator that can handle any data type
export const createColumn = <TData = unknown,>(
  accessorKey: string,
  header: string,
  options: BaseColumnOptions = {}
): ColumnDef<TData> => {
  const {
    minWidth = 120,
    maxWidth = 300,
    enableSorting = true,
    enableFiltering = true,
    enableResizing = true,
    enableHiding = true,
    cellRenderer,
    headerRenderer,
    filterFn: _filterFn,
    sortFn: _sortFn,
    className = '',
    headerClassName = '',
    cellClassName = '',
  } = options;

  // If accessorKey contains a dot, use accessorFn to handle flat keys correctly
  // TanStack Table's accessorKey treats dots as nested paths, but our data uses flat keys
  const hasDot = accessorKey.includes('.');

  return {
    // Use accessorFn when key contains dots to properly access flat keys
    ...(hasDot
      ? {
          accessorFn: (row: TData) => {
            const record = row as Record<string, unknown>;
            return record[accessorKey];
          },
        }
      : { accessorKey }),
    id: accessorKey, // Always set id for stable column identification
    header: ({ column }) => {
      if (headerRenderer) {
        return headerRenderer(header);
      }
      return <SortableHeader column={column}>{header}</SortableHeader>;
    },
    size: minWidth,
    minSize: minWidth,
    maxSize: maxWidth,
    enableSorting,
    enableColumnFilter: enableFiltering,
    enableResizing,
    enableHiding,
    cell: ({ getValue, row }) => {
      const value = getValue();
      if (cellRenderer) {
        return cellRenderer(value, row.original);
      }
      return (
        <div
          className={`text-sm text-foreground ${cellClassName}`}
          style={{
            maxWidth: `${maxWidth}px`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {String(value ?? '')}
        </div>
      );
    },
    meta: {
      className,
      headerClassName,
      cellClassName,
    },
  };
};

// Text column with enhanced options
export const createTextColumn = <TData = unknown,>(
  accessorKey: string,
  header: string,
  options: BaseColumnOptions & {
    multiline?: boolean;
    maxLines?: number;
    truncate?: boolean;
  } = {}
): ColumnDef<TData> => {
  const {
    multiline = false,
    maxLines = 3,
    truncate = true,
    cellRenderer,
    ...baseOptions
  } = options;

  return createColumn<TData>(accessorKey, header, {
    ...baseOptions,
    cellRenderer:
      cellRenderer ??
      ((value) => {
        if (multiline) {
          return (
            <div
              className="text-sm text-foreground"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: maxLines,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {String(value ?? '')}
            </div>
          );
        }
        return (
          <div
            className="text-sm text-foreground"
            style={{
              maxWidth: truncate ? '300px' : 'none',
              overflow: truncate ? 'hidden' : 'visible',
              textOverflow: truncate ? 'ellipsis' : 'unset',
              whiteSpace: truncate ? 'nowrap' : 'normal',
            }}
          >
            {String(value ?? '')}
          </div>
        );
      }),
  });
};

// Number column with formatting options
export const createNumberColumn = <TData = unknown,>(
  accessorKey: string,
  header: string,
  options: BaseColumnOptions & {
    format?: (value: number) => string;
    locale?: string;
    currency?: string;
    precision?: number;
    showSign?: boolean;
  } = {}
): ColumnDef<TData> => {
  const {
    format,
    locale = 'en-US',
    currency,
    precision = 2,
    showSign = false,
    cellRenderer,
    ...baseOptions
  } = options;

  return createColumn<TData>(accessorKey, header, {
    ...baseOptions,
    cellRenderer:
      cellRenderer ??
      ((value) => {
        if (value === null || value === undefined || value === '') {
          return <span className="text-sm text-muted-foreground/60">-</span>;
        }

        if (format) {
          return (
            <span className="font-mono text-sm text-foreground">
              {format(Number(value))}
            </span>
          );
        }

        const parsed = typeof value === 'number' ? value : parseFloat(String(value));
        const numValue = isNaN(parsed) ? 0 : parsed;

        if (currency) {
          return (
            <span className="font-mono text-sm font-medium text-primary">
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency,
                minimumFractionDigits: precision,
                maximumFractionDigits: precision,
              }).format(numValue)}
            </span>
          );
        }

        return (
          <span className="font-mono text-sm text-foreground">
            {new Intl.NumberFormat(locale, {
              minimumFractionDigits: precision,
              maximumFractionDigits: precision,
              signDisplay: showSign ? 'always' : 'auto',
            }).format(numValue)}
          </span>
        );
      }),
  });
};

// Date column with flexible formatting
export const createDateColumn = <TData = unknown,>(
  accessorKey: string,
  header: string,
  options: BaseColumnOptions & {
    format?: (value: string | Date) => string;
    showTime?: boolean;
    relative?: boolean;
  } = {}
): ColumnDef<TData> => {
  const {
    format,
    showTime = false,
    relative = false,
    cellRenderer,
    ...baseOptions
  } = options;

  return createColumn<TData>(accessorKey, header, {
    ...baseOptions,
    cellRenderer:
      cellRenderer ??
      ((value) => {
        if (format) {
          return (
            <span className="text-sm text-muted-foreground">
              {format(String(value))}
            </span>
          );
        }

        if (!value) {
          return <span className="text-sm text-muted-foreground/60">-</span>;
        }

        let date: Date;
        // Handle DuckDB value objects that weren't serialized by the API
        // DuckDBDateValue has {days: number} property
        if (
          typeof value === 'object' &&
          value !== null &&
          'days' in value &&
          typeof (value as Record<string, unknown>).days === 'number'
        ) {
          const days = (value as { days: number }).days;
          const unixMs = days * 86400 * 1000;
          date = new Date(unixMs);
        }
        // Handle numeric date values (days since Unix epoch from Parquet/DuckDB)
        // Parquet stores dates as INT32 (days since 1970-01-01)
        // Range 0-50000 covers dates from 1970 to ~2106
        else if (typeof value === 'number' && value >= 0 && value < 50000) {
          // Convert Unix days to JavaScript Date
          const unixMs = value * 86400 * 1000;
          date = new Date(unixMs);
        } else {
          date = new Date(String(value));
        }

        // Handle invalid dates
        if (isNaN(date.getTime())) {
          return <span className="text-sm text-muted-foreground/60">-</span>;
        }

        if (relative) {
          const now = new Date();
          const diffInSeconds = Math.floor(
            (now.getTime() - date.getTime()) / 1000
          );

          if (diffInSeconds < 60) return 'Just now';
          if (diffInSeconds < 3600)
            return `${Math.floor(diffInSeconds / 60)}m ago`;
          if (diffInSeconds < 86400)
            return `${Math.floor(diffInSeconds / 3600)}h ago`;
          if (diffInSeconds < 2592000)
            return `${Math.floor(diffInSeconds / 86400)}d ago`;

          // Fall back to browser locale for older dates
          return date.toLocaleDateString();
        }

        // Use browser's locale for date/time formatting
        const dateOptions: Intl.DateTimeFormatOptions = {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          ...(showTime && {
            hour: '2-digit',
            minute: '2-digit',
          }),
        };

        return (
          <span className="text-sm text-muted-foreground">
            {date.toLocaleDateString(undefined, dateOptions)}
          </span>
        );
      }),
  });
};

// Boolean column with custom labels and styling
export const createBooleanColumn = <TData = unknown,>(
  accessorKey: string,
  header: string,
  options: BaseColumnOptions & {
    trueLabel?: string;
    falseLabel?: string;
    trueVariant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info' | 'muted';
    falseVariant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info' | 'muted';
    showIcon?: boolean;
  } = {}
): ColumnDef<TData> => {
  const {
    trueLabel = 'Yes',
    falseLabel = 'No',
    trueVariant = 'success',
    falseVariant = 'destructive',
    showIcon = false,
    cellRenderer,
    ...baseOptions
  } = options;

  return createColumn<TData>(accessorKey, header, {
    ...baseOptions,
    cellRenderer:
      cellRenderer ??
      ((value) => {
        const isTrue = Boolean(value);
        return (
          <Badge variant={isTrue ? trueVariant : falseVariant}>
            {showIcon && <span className="mr-1">{isTrue ? '✓' : '✗'}</span>}
            {isTrue ? trueLabel : falseLabel}
          </Badge>
        );
      }),
  });
};

// Currency column (specialized number column)
export const createCurrencyColumn = <TData = unknown,>(
  accessorKey: string,
  header: string,
  options: BaseColumnOptions & {
    currency?: string;
    locale?: string;
    precision?: number;
    showSign?: boolean;
  } = {}
): ColumnDef<TData> => {
  const {
    currency = 'USD',
    locale = 'en-US',
    precision = 2,
    showSign = false,
    ...baseOptions
  } = options;

  return createNumberColumn<TData>(accessorKey, header, {
    ...baseOptions,
    currency,
    locale,
    precision,
    showSign,
  });
};

// Status column with predefined status types
export const createStatusColumn = <TData = unknown,>(
  accessorKey: string,
  header: string,
  options: BaseColumnOptions & {
    statusMap?: Record<string, {
      label: string;
      variant: 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info' | 'muted';
      icon?: React.ReactNode;
    }>;
    defaultStatus?: {
      label: string;
      variant: 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info' | 'muted';
      icon?: React.ReactNode;
    };
  } = {}
): ColumnDef<TData> => {
  const {
    statusMap = {
      active: { label: 'Active', variant: 'success' as const },
      inactive: { label: 'Inactive', variant: 'muted' as const },
      pending: { label: 'Pending', variant: 'warning' as const },
      error: { label: 'Error', variant: 'destructive' as const },
    },
    defaultStatus = { label: 'Unknown', variant: 'muted' as const },
    cellRenderer,
    ...baseOptions
  } = options;

  return createColumn<TData>(accessorKey, header, {
    ...baseOptions,
    cellRenderer:
      cellRenderer ??
      ((value) => {
        const status = statusMap[String(value).toLowerCase()] ?? defaultStatus;
        return (
          <Badge variant={status.variant}>
            {status.icon}
            {status.label}
          </Badge>
        );
      }),
  });
};

// Image column for displaying images
export const createImageColumn = <TData = unknown,>(
  accessorKey: string,
  header: string,
  options: BaseColumnOptions & {
    width?: number;
    height?: number;
    fallback?: string;
    alt?: string;
  } = {}
): ColumnDef<TData> => {
  const {
    width = 40,
    height = 40,
    fallback = 'No image',
    alt = 'Image',
    cellRenderer,
    ...baseOptions
  } = options;

  return createColumn<TData>(accessorKey, header, {
    ...baseOptions,
    minWidth: width + 20,
    maxWidth: width + 20,
    cellRenderer:
      cellRenderer ??
      ((value) => {
        if (!value) {
          return (
            <div className="flex items-center justify-center text-xs text-muted-foreground">
              {fallback}
            </div>
          );
        }

        return (
          <img
            src={String(value)}
            alt={alt}
            className="rounded object-cover"
            style={{ width, height }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
            }}
          />
        );
      }),
  });
};

// Action column for buttons and actions
export const createActionColumn = <TData = unknown,>(
  header: string,
  options: BaseColumnOptions & {
    actions: Array<{
      label: string;
      icon?: React.ReactNode;
      onClick: (row: TData) => void;
      variant?:
        | 'default'
        | 'destructive'
        | 'outline'
        | 'secondary'
        | 'ghost'
        | 'link';
      disabled?: (row: TData) => boolean;
    }>;
  } = { actions: [] }
): ColumnDef<TData> => {
  const {
    actions,
    filterFn: _filterFn,
    sortFn: _sortFn,
    ...baseOptions
  } = options;

  return {
    id: 'actions',
    header,
    accessorFn: () => null,
    enableSorting: false,
    enableColumnFilter: false,
    enableResizing: false,
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-2">
          {actions.map((action, index) => {
            const isDisabled = action.disabled?.(row.original) ?? false;
            return (
              <button
                key={index}
                onClick={() => !isDisabled && action.onClick(row.original)}
                disabled={isDisabled}
                className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors ${
                  isDisabled
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:bg-muted'
                }`}
              >
                {action.icon}
                {action.label}
              </button>
            );
          })}
        </div>
      );
    },
    ...baseOptions,
  };
};

// Name column with icon, name, and optional description
export const createNameColumn = <TData = unknown,>(
  accessorKey: string,
  header: string,
  options: BaseColumnOptions & {
    descriptionKey?: string;
    icon?: React.ComponentType<{ className?: string }>;
    iconClassName?: string;
    nameClassName?: string;
    descriptionClassName?: string;
    clickable?: boolean;
  } = {}
): ColumnDef<TData> => {
  const {
    descriptionKey,
    icon: Icon,
    iconClassName = 'h-5 w-5 text-primary',
    nameClassName = 'font-semibold truncate',
    descriptionClassName = 'text-sm text-muted-foreground truncate',
    clickable = false,
    cellRenderer,
    ...baseOptions
  } = options;

  return createColumn<TData>(accessorKey, header, {
    ...baseOptions,
    minWidth: baseOptions.minWidth ?? 200,
    maxWidth: baseOptions.maxWidth ?? 400,
    cellRenderer:
      cellRenderer ??
      ((value, original) => {
        const name = String(value ?? '');
        const description = descriptionKey
          ? String((original as Record<string, unknown>)[descriptionKey] ?? '')
          : null;

        return (
          <div
            className={`flex items-center gap-3 ${clickable ? 'cursor-pointer' : ''}`}
          >
            {Icon && (
              <div className="bg-primary/10 rounded-lg p-2 shrink-0">
                <Icon className={iconClassName} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className={nameClassName}>{name}</div>
              {description && (
                <div className={descriptionClassName}>{description}</div>
              )}
            </div>
          </div>
        );
      }),
  });
};
